var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Record-picker subfield input.
 * Renders a Stripes Selection (typeahead) + icon button for modal browser.
 * Works for all record types: vocabularies, geographic authorities, subjects, etc.
 * Stores both the display value AND the sourceAuthorityId (FK to selected record).
 *
 * Supports two modes:
 * - Closed vocabulary (default): user must pick from the list.
 * - Open vocabulary (allowFreeText: true): the dropdown shows a visually
 *   distinct "+ Add new term: {text}" affordance whenever the typed text
 *   doesn't match any existing item. Click opens a {@link VocabularyForm}
 *   modal pre-seeded with the typed text and the field's vocabulary type.
 *   On successful create the new vocabulary item is treated identically to
 *   picking an existing one — the form receives a proper `sourceAuthorityId`
 *   (FK), eliminating the previous orphan free-text state (TASK-086).
 *   A target with no create path of its own (SUBJECT, CZNA-1002) offers
 *   "+ Použít jako text: {text}" instead, which stores the text without an FK.
 *   So does a register subfield that sets `literalFreeText` (680$i, CZNA-994):
 *   open, but a bypass rather than a way to add terms.
 *
 * A stored value may carry no `sourceAuthorityId` at all — that is the normal
 * state of the whole imported corpus (CZNA-798: 1 021 214 subfield occurrences on
 * 375$a / 377$a / 500$i alone, none of which has an FK), plus drafts written
 * before TASK-086. The READ path renders such a value as the current selection
 * regardless of `allowFreeText`; only NEW input is gated by the mode, so a closed
 * picker still refuses free text while showing what the record actually holds.
 *
 * A pick on the `$a` of a name field that declares the other parts of the name (500 $q $b
 * $c $d, AUTHOR_WORK 100/400) fills them part by part from the picked authority's heading
 * instead of writing the whole heading into `$a` (CZNA-1430, `utils/linkedHeadingCopy.ts`).
 * In a field naming a work, the linked name is then locked: shown, not edited, until
 * "Odpojit" drops the link and keeps the text (CZNA-1474).
 *
 * Replaces VocabularySubfield — unified on records API.
 */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Selection, IconButton } from '@folio/stripes/components';
import { Controller, useWatch, } from 'react-hook-form';
import { useOkapiKy } from '@folio/stripes/core';
import { resolveLabel } from '../renderer/labelResolver';
import useRecordPickerOptions from '../../../hooks/useRecordPickerOptions';
import { fetchRecord } from '../../../api/recordService';
import { insertsSuffix } from '../../../utils/pickerRegister';
import { useVocabularyTypes } from '../../../hooks/useVocabularyTypes';
import useAllowedProposalActions from '../../../hooks/useAllowedProposalActions';
import RecordPickerModal from './RecordPickerModal';
import ClearFieldButton from './ClearFieldButton';
import VocabularyForm from '../../VocabularyForm';
import { VOCABULARY_TYPE_CONFIG } from '../../../types/api';
import { useProposeAuthority } from '../ProposeAuthorityContext';
import { PROPOSE_AUTHORITY_TARGETS, PUBLISHING_PROPOSE_TARGETS } from '../proposeAuthorityFlow';
import { storesVocabularyCode, vocabularyCodeOf, vocabularyOptionLabel, vocabularyStoredValue, } from '../utils/vocabularyCodeValue';
import { applyLinkedHeadingCopy, clearedLinkedHeading, copiesLinkedHeading, linkedHeadingCopy, locksLinkedName, } from '../utils/linkedHeadingCopy';
import { subfieldRowStyle as rowStyle, subfieldCodeStyle as codeStyle, subfieldLabelStyle as labelStyle, subfieldReadOnlyInputStyle, subfieldActionsStyle, subfieldInputColumnOffset, subfieldLinkButtonStyle, } from './subfieldStyles';
import css from './subfieldPicker.css';
/** The Selection's cell takes the input column; the icons go to the shared slot (CZNA-1572). */
var pickerCellStyle = { flex: 1, minWidth: 0 };
/**
 * The row wraps so a load error gets a line of its own under the picker: kept inside the
 * cell it made the cell taller, and the centred slot slid down away from the control.
 */
var PICKER_ROW_STYLE = __assign(__assign({}, rowStyle), { flexWrap: 'wrap' });
/** The load errors' line, from the input column; `gap` already separates it from the row. */
var PICKER_ALERTS_STYLE = {
    flexBasis: '100%',
    paddingLeft: subfieldInputColumnOffset,
    marginTop: '-6px',
};
/** "Odpojit" under the linked name, from the input column — like "Načíst titulek" (CZNA-1572). */
var UNLINK_ROW_STYLE = {
    paddingLeft: subfieldInputColumnOffset,
    marginTop: '-4px',
    marginBottom: '8px',
};
/**
 * Sentinel prefix for the "+ Add new term: …" option (TASK-086). Distinct from
 * any real UUID so we can detect the click and open the create modal instead of
 * treating the value as a record id.
 */
var ADD_NEW_TERM_PREFIX = '__add_new__';
/**
 * Sentinel prefix for a STORED value that has no `sourceAuthorityId`, so
 * `Selection` has an option to render instead of falling back to the
 * placeholder. Same job as `UNLINKED_PREFIX` in `AuthorityDefaultValueEditor`
 * (a different literal, because neither is ever persisted nor compared across
 * the two), and it can never collide with a UUID.
 *
 * CZNA-798: this is NOT a legacy shape. No imported record carries an FK, so
 * this is how the majority of the corpus reaches the form; it is only ever a
 * DISPLAY value and is never written back into the form state (see
 * `handleSelectionChange`), because a sentinel stored as `source_authority_id`
 * would be a fabricated reference.
 *
 * It is ALSO what keeps such a value linkable, which is why the selection points
 * at the sentinel even when the register holds the same heading. `Selection`
 * suppresses `onChange` when the clicked option's value equals the current one
 * (`stripes-components/lib/Selection/Selection.js:190-194`), so selecting the
 * register's own UUID would make a click on that row a silent no-op — the
 * unlinked value could then never be linked from the dropdown at all, which is
 * the majority of the corpus. The sentinel differs from every register option by
 * construction, so the click always reaches `handleSelectionChange`.
 */
var NO_FK_VALUE_PREFIX = '__nofk__';
/**
 * CZNA-1002: sentinel prefix for the "+ Použít jako text: …" option — the third
 * way `allowFreeText` can be honoured, for a target that has neither a register to
 * add a term to nor an authority form to propose one in (today SUBJECT, on 368$c).
 * The click writes the typed text with NO `sourceAuthorityId`: the same shape as a
 * stored unlinked value (CZNA-798), which the save already writes back unchanged.
 */
var LITERAL_TEXT_PREFIX = '__literal__';
/**
 * CZNA-197: sentinel prefix for the "+ Navrhnout založení GA: …" option. Distinct
 * from {@link ADD_NEW_TERM_PREFIX} (vocabulary create) so the same dropdown can,
 * in principle, host both affordances and {@link handleSelectionChange} routes to
 * the right modal. Used only for authority picker targets (GEOGRAPHIC in v1).
 */
var PROPOSE_AUTHORITY_PREFIX = '__propose_authority__';
/**
 * CZNA-197 / CZNA-241 / CZNA-319 / CZNA-321: which picker targets offer the
 * propose/create affordance is decided by three things, none of them here:
 *
 *  - the target TYPE — {@link PROPOSE_AUTHORITY_TARGETS} (GEOGRAPHIC, PERSONAL, CORPORATE, MEETING);
 *  - the SUBFIELD — `allowFreeText: true` in the form-config (so 500$a offers it and
 *    682$a, "replaced by", does not);
 *  - for a type whose child form publishes ({@link PUBLISHING_PROPOSE_TARGETS}), the
 *    BACKEND — `publish` in `GET proposals/allowed-actions`. The affordance promises a
 *    published record, so it is offered only where one would actually be published
 *    (CZNA-320 mantinel, CZNA-598: the FE does not recompute that).
 *
 * The click calls `proposeAuthority(subfieldPath, seed, recordType)` from
 * {@link useProposeAuthority}; the form host (RecordForm) snapshots the parent form to
 * sessionStorage and navigates to a SEPARATE create route for that type (its own root
 * Paneset). On return the parent rehydrates and the new authority's FK + heading land
 * in this subfield.
 */
/**
 * Visual style for the "+ Add new term" dropdown row — bold, accent colour,
 * separator BELOW the row (the affordance sits at the TOP of the dropdown
 * since TASK-145, so the separator goes underneath to divide it from the
 * filtered vocabulary suggestions that follow).
 */
var ADD_NEW_TERM_LABEL_STYLE = {
    fontWeight: 600,
    color: '#1960a4',
};
var ADD_NEW_TERM_ROW_STYLE = {
    borderBottom: '1px solid #e0e0e0',
    paddingBottom: '4px',
    marginBottom: '4px',
};
/**
 * Static-options picker — same visual identity as RecordPickerSubfield
 * (Stripes Selection + `$code` row), but driven by a static enum list from
 * `subfieldDef.selectOptions`. No API fetch, no modal browser, no
 * "+ Add new term" affordance. Used for small immutable enums that shouldn't
 * be a backend vocabulary (e.g. family qualifier rod/rodina — TASK-115).
 *
 * Triggered when `subfieldDef.selectOptions` is set on a `record-picker` /
 * `vocabulary` subfield. The `$code` prefix is hidden when the subfield is
 * `formOnly: true`, since synthetic UI-only slots have no real MARC code.
 */
var StaticOptionsPicker = function (_a) {
    var subfieldDef = _a.subfieldDef, pathPrefix = _a.pathPrefix, control = _a.control;
    var intl = useIntl();
    var subfieldPath = "".concat(pathPrefix, ".").concat(subfieldDef.code);
    var dataOptions = useMemo(function () {
        var _a;
        return ((_a = subfieldDef.selectOptions) !== null && _a !== void 0 ? _a : []).map(function (opt) { return ({
            value: opt.value,
            label: resolveLabel(opt, intl),
        }); });
    }, [subfieldDef.selectOptions, intl]);
    return (React.createElement(Controller, { name: subfieldPath, control: control, render: function (_a) {
            var field = _a.field;
            var subVal = (field.value || { value: '' });
            var handleChange = function (value) {
                field.onChange(__assign(__assign({}, subVal), { value: value }));
            };
            return (
            // CZNA-962: `data-field-path` is the SAME handle the API-backed picker
            // below carries, so a caller addresses either flavour the one way. The
            // `data-testid` it replaced was built from the subfield CODE alone, so
            // two instances of one repeatable field — and any two fields sharing a
            // code, which is most of them — resolved to the same node.
            React.createElement("div", { style: rowStyle, "data-field-path": subfieldPath },
                !subfieldDef.formOnly && React.createElement("span", { style: codeStyle },
                    "$",
                    subfieldDef.code),
                React.createElement("span", { style: labelStyle },
                    resolveLabel(subfieldDef, intl),
                    subfieldDef.required && React.createElement("span", { style: { color: '#d0021b', marginLeft: '2px' } }, "*")),
                React.createElement("div", { className: css.picker, style: pickerCellStyle },
                    React.createElement(Selection, { dataOptions: dataOptions, value: subVal.value || '', onChange: handleChange, placeholder: intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.search' }), marginBottom0: true })),
                React.createElement("span", { style: subfieldActionsStyle },
                    React.createElement(ClearFieldButton, { visible: !!subVal.value, onClear: function () { return field.onChange(__assign(__assign({}, subVal), { value: '' })); } }))));
        } }));
};
/** CZNA-1033: the "→ vloží se …" half of a variant row, set off from what was found. */
var INSERTS_SUFFIX_STYLE = {
    color: '#666',
};
/** CZNA-1474: a linked work's name — the look of a disabled subfield input. */
var LOCKED_NAME_STYLE = subfieldReadOnlyInputStyle;
var CODE_LOAD_ERROR_STYLE = {
    color: '#d0021b',
    fontSize: '0.85em',
    marginTop: '2px',
};
/**
 * The API-backed picker: register window, modal browser, create affordances.
 * Its own component (CZNA-1380) so the static-options branch in
 * {@link RecordPickerSubfield} is a choice between two components rather than an
 * early return in the middle of this one's hooks — `react-hooks/rules-of-hooks`
 * is an error, and a hook order that depends on the subfield's shape is the bug it
 * exists to catch.
 */
var RegisterPicker = function (_a) {
    var _b, _c;
    var subfieldDef = _a.subfieldDef, pathPrefix = _a.pathPrefix, control = _a.control, fieldDef = _a.fieldDef, getValues = _a.getValues, setValue = _a.setValue;
    var intl = useIntl();
    var ky = useOkapiKy();
    // CZNA-241: bridge to the form host (RecordForm) which owns getValues/
    // activeTabIndex and performs the snapshot + navigation. The picker only knows
    // its own subfield path + the typed seed.
    var proposeAuthority = useProposeAuthority().proposeAuthority;
    // CZNA-940: the registry half of "does this register store a code" — the same union the
    // backend computes (static list ∪ `vocabulary_type.code_required`). One
    // request for the whole app: react-query keys it on the arguments, and every picker
    // instance asks with the same ones.
    var _d = useVocabularyTypes(), vocabularyTypes = _d.vocabularyTypes, vocabularyTypesLoading = _d.isLoading, vocabularyTypesError = _d.isError;
    // CZNA-321: the backend's answer to "would a new proposal of this type be published
    // directly". Same react-query key in every picker, so one request per screen.
    var canPublishNewProposalOfType = useAllowedProposalActions().canPublishNewProposalOfType;
    var _e = useState(false), modalOpen = _e[0], setModalOpen = _e[1];
    var _f = useState(false), vocabularyFormOpen = _f[0], setVocabularyFormOpen = _f[1];
    var _g = useState(''), vocabularyFormSeed = _g[0], setVocabularyFormSeed = _g[1];
    var _h = useState(null), selectedOption = _h[0], setSelectedOption = _h[1];
    var _j = useState(''), filterText = _j[0], setFilterText = _j[1];
    // CZNA-1430: bumped by a literal pick to remount Selection — see the literal branch below.
    var _k = useState(0), selectionKey = _k[0], setSelectionKey = _k[1];
    // CZNA-1033: reading the picked term's code failed, so nothing was stored.
    var _l = useState(false), codeLoadFailed = _l[0], setCodeLoadFailed = _l[1];
    // CZNA-1430: reading the picked authority's heading failed, so nothing was stored.
    var _m = useState(false), headingLoadFailed = _m[0], setHeadingLoadFailed = _m[1];
    var syncedAuthorityIdRef = useRef(undefined);
    // CZNA-798: the sync effect used to key on the FK alone. That was enough while the
    // no-FK branch was unreachable for closed pickers; now it is the branch the whole
    // imported corpus takes, and a write that changes `value` while leaving the FK empty
    // (`reset()` on return from the propose-authority route is that shape) would leave a
    // stale sentinel selected — pointing at an option that no longer exists, which
    // `Selection` renders as a blank field. Exactly the symptom this ticket removes.
    var syncedValueRef = useRef(undefined);
    var handlePickRef = useRef(function () { });
    // CZNA-1033: bumped by every write to the subfield. A code read started for an
    // earlier pick must not land over a later pick or a clear.
    var pickSeqRef = useRef(0);
    // CZNA-1474: "Odpojit" unmounts the button it was pressed on; focus goes to the picker
    // that replaces it, not to the page.
    var focusPickerRef = useRef(false);
    var rowRef = useRef(null);
    var recordType = subfieldDef.recordType || subfieldDef.vocabularyType || '';
    var preload = (_b = subfieldDef.pickerPreload) !== null && _b !== void 0 ? _b : false;
    var allowFreeText = (_c = subfieldDef.allowFreeText) !== null && _c !== void 0 ? _c : false;
    var subfieldPath = "".concat(pathPrefix, ".").concat(subfieldDef.code);
    // CZNA-1430: a pick fills the name part by part — needs the field and a way to write it.
    var copiesHeading = !!getValues && !!setValue
        && copiesLinkedHeading(fieldDef === null || fieldDef === void 0 ? void 0 : fieldDef.tag, subfieldDef.code, fieldDef === null || fieldDef === void 0 ? void 0 : fieldDef.subfields);
    // CZNA-1474: and in a field naming a work, a linked name is locked until "Odpojit".
    var locksName = copiesHeading && locksLinkedName(fieldDef);
    // CZNA-994: an OPEN register the cataloguer may bypass without growing it — 680$i,
    // whose 271 distinct off-register values in CORPORATE/MEETING are free sentences, not
    // missing terms.
    // Typed text is kept as text (the CZNA-1002 row below) instead of becoming a term.
    var literalFreeText = allowFreeText && subfieldDef.literalFreeText === true;
    // VocabularyForm only knows how to create VOCABULARY_* types — that lightweight
    // create path stays as-is for vocabularies.
    // CZNA-1310 (decided by CZNA-958): an OPEN vocabulary grows from this form, a CLOSED one
    // (ISO 639-2, MARC codes, NK methodology) does not — the backend refuses a non-admin with
    // 403. A type with no registry row is not CLOSED, the same rule the backend applies. Until
    // the registry has answered, we cannot tell, so the button is withheld rather than offered
    // for a create that would be refused.
    var vocabularyIsClosed = vocabularyTypes
        .some(function (t) { return t.code === recordType && t.validationType === 'CLOSED'; });
    // CZNA-1470 — a type created in Nastavení → Slovníky has a registry row but no
    // VOCABULARY_TYPE_CONFIG entry; VocabularyForm offers it, so the term may be added.
    var vocabularyIsKnown = Boolean(VOCABULARY_TYPE_CONFIG[recordType])
        || vocabularyTypes.some(function (t) { return t.code === recordType; });
    var supportsAddVocabulary = allowFreeText
        && !literalFreeText
        && vocabularyIsKnown
        && !vocabularyTypesLoading
        && !vocabularyTypesError
        && !vocabularyIsClosed;
    // CZNA-197 / CZNA-241 / CZNA-321: authority picker targets get a "Navrhnout
    // založení" / "Založit autoritu" affordance that navigates to the separate create
    // route via the propose-authority context (no overlay over the parent editor).
    // A publishing target offers it only when the backend says it would publish.
    var createsPublished = PUBLISHING_PROPOSE_TARGETS.has(recordType);
    var supportsProposeAuthority = allowFreeText
        && PROPOSE_AUTHORITY_TARGETS.has(recordType)
        && (!createsPublished || canPublishNewProposalOfType(recordType));
    // CZNA-1002: a free-text picker whose target has no create path at all keeps the
    // typed text as it is. Gated on the TYPE, not on the two flags above: a propose
    // target the backend will not let this user publish to stays closed, as before.
    // Every VOCABULARY_* type is out, listed in VOCABULARY_TYPE_CONFIG or not: 4xx$2
    // (VOCABULARY_THESAURUS) carries `allowFreeText` with no create path and must stay
    // a pick from the register — unless the subfield opts in by `literalFreeText` (CZNA-994).
    var supportsLiteralText = literalFreeText
        || (allowFreeText
            && !recordType.startsWith('VOCABULARY_')
            && !PROPOSE_AUTHORITY_TARGETS.has(recordType));
    // Drives asyncFilter/onFilter wiring + whether the dropdown prepends a create row.
    var supportsAddNew = supportsAddVocabulary || supportsProposeAuthority || supportsLiteralText;
    var formValue = useWatch({ control: control, name: subfieldPath });
    useEffect(function () {
        var _a, _b;
        if (!focusPickerRef.current)
            return;
        focusPickerRef.current = false;
        (_b = (_a = rowRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button, input')) === null || _b === void 0 ? void 0 : _b.focus();
    }, [selectionKey]);
    useEffect(function () {
        var fvId = formValue === null || formValue === void 0 ? void 0 : formValue.sourceAuthorityId;
        var fvValue = formValue === null || formValue === void 0 ? void 0 : formValue.value;
        if (fvId !== syncedAuthorityIdRef.current || fvValue !== syncedValueRef.current) {
            syncedAuthorityIdRef.current = fvId;
            syncedValueRef.current = fvValue;
            if (fvId && fvValue) {
                setSelectedOption({ value: fvId, label: fvValue, heading: fvValue, code: null });
            }
            else if (fvValue && !fvId) {
                // CZNA-798: a stored value with no FK — the normal shape of every imported
                // record, not just of pre-TASK-086 drafts. Render it as the current selection
                // so the field does not read as empty; this used to be gated on
                // `allowFreeText`, which left 1,02 M occurrences (375$a, 377$a, 500$i) looking
                // unfilled. Writing a new value is still gated by the mode — the guard belongs
                // on input, not on display.
                setSelectedOption({
                    value: "".concat(NO_FK_VALUE_PREFIX).concat(fvValue),
                    // Labelled apart from the register's own row, which may carry the same
                    // heading: one is what the record stores, the other is what would link it.
                    // `heading` stays the raw stored text so the typed-text filter still matches.
                    label: intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.unlinkedValue' }, { value: fvValue }),
                    heading: fvValue,
                    // The stored value is all we know here; whether it is a code or a heading is
                    // exactly what the missing FK leaves unresolved (CZNA-798). Annotated because
                    // the build config has `strictNullChecks: false`, where a bare `null` widens to
                    // `any` and trips `noImplicitAny` (TS7018).
                    code: null,
                });
            }
            else {
                setSelectedOption(null);
            }
        }
    }, [formValue, intl]);
    var _o = useRecordPickerOptions({
        recordType: recordType,
        preload: preload,
        alsoRecordTypes: subfieldDef.alsoRecordTypes,
    }), searchOptions = _o.dataOptions, loading = _o.loading, registerFailed = _o.failed, baseHandleFilter = _o.handleFilter, asyncFilter = _o.asyncFilter;
    // Wrap filter handler so we can show the "+ Add new term" affordance using the typed text.
    var handleFilter = useCallback(function (text) {
        setFilterText(text);
        baseHandleFilter(text);
    }, [baseHandleFilter]);
    // Build options list — the register window, then optionally prepend "+ Add new term".
    //
    // CZNA-1033: the window is NOT filtered here any more. It is the register opened at the
    // typed text, in register order, and that order is the point: a substring filter over
    // `heading`/`label` (TASK-145) used to run on top of it, matched the qualifier
    // "(Česko)" of rows that had nothing to do with "česko", and differed from the server
    // on diacritics. Selection still runs with asyncFilter=true, so it does not filter either.
    //
    // Only the currently-selected option is matched against the typed text: it is not a
    // register row, and while the user types it is kept only when it still matches.
    var dataOptions = useMemo(function () {
        var trimmed = filterText.trim();
        var needle = trimmed.toLowerCase();
        // CZNA-798: a stored value with no FK usually names a term the register also
        // holds, so the list can carry the same heading twice — once as the unlinked
        // value the record actually stores, once as the register row that would link
        // it. That is deliberate, and the two are told apart by the unlinked row's
        // label (`recordPicker.unlinkedValue`), not by hiding one of them: collapsing
        // them onto the register's UUID is what made the link unreachable, because
        // `Selection` drops an onChange whose value equals the current selection.
        var selectedMatches = selectedOption && (!needle
            || selectedOption.heading.toLowerCase().includes(needle)
            || selectedOption.label.toLowerCase().includes(needle));
        // CZNA-1033: a register row carries no code, the pick has just read it. The row of the
        // picked record takes it over, so the field shows "name (code)" as it did before.
        var pickedCode = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.code;
        var rows = pickedCode
            ? searchOptions.map(function (o) { return (o.value === selectedOption.value && o.code === undefined
                ? __assign(__assign({}, o), { code: pickedCode, label: vocabularyOptionLabel(o.label, pickedCode) }) : o); })
            : searchOptions;
        var filtered = selectedMatches && !rows.some(function (o) { return o.value === selectedOption.value; })
            ? __spreadArray([selectedOption], rows, true) : __spreadArray([], rows, true);
        // CZNA-1033: an unread register is not an empty one — offering to create the
        // typed text then would offer a duplicate of a term that may well exist.
        if (supportsAddNew && trimmed && !registerFailed) {
            // The affordance is suppressed when the typed text matches an item already
            // in the list (approved record OR proposal — searchOptions carries both for
            // GEOGRAPHIC since CZNA-197), so we never prompt to create a duplicate.
            // CZNA-1033: also the heading a row was FOUND under — typing an existing variant
            // must not offer to create it as a new term.
            var alreadyInList = filtered.some(function (o) { return o.heading.toLowerCase() === needle
                || ('found' in o && typeof o.found === 'string' && o.found.toLowerCase() === needle); });
            // CZNA-1430: a name is not a person. `Dvořák, Antonín` is the whole heading of four
            // dateless persons, and an author by that name may be none of them — so a name field
            // keeps its text row beside them instead of leaving only a link to a namesake.
            if (!alreadyInList || (supportsLiteralText && copiesHeading)) {
                // The create affordance sits at the TOP of the dropdown (TASK-145) so it's
                // visible without scrolling past suggestions. Only shown when the user has
                // typed at least one character — an empty input must not prompt to create.
                // CZNA-197: authority targets prepend "+ Navrhnout založení GA: …" (opens
                // the in-tab GA modal); vocabulary targets keep "+ Add new term: …";
                // CZNA-1002: a target with neither offers "+ Použít jako text: …".
                var prefix = ADD_NEW_TERM_PREFIX;
                var labelId = 'ui-czna-authorities.recordPicker.addNewTerm';
                if (supportsLiteralText) {
                    prefix = LITERAL_TEXT_PREFIX;
                    labelId = 'ui-czna-authorities.recordPicker.useAsText';
                }
                else if (supportsProposeAuthority) {
                    prefix = PROPOSE_AUTHORITY_PREFIX;
                    // CZNA-321: a publishing target founds the authority, it does not
                    // propose it — the label must not promise a review that won't happen.
                    labelId = createsPublished
                        ? 'ui-czna-authorities.recordPicker.createAuthority'
                        : 'ui-czna-authorities.recordPicker.proposeAuthority';
                }
                var affordance = {
                    value: "".concat(prefix).concat(trimmed),
                    label: intl.formatMessage({ id: labelId }, { text: trimmed }),
                    heading: trimmed,
                    // Not a register term — nothing to code. Present so every row of the merged
                    // list has the same shape (CZNA-940); annotated for the same TS7018 reason as
                    // above.
                    code: null,
                };
                return __spreadArray([affordance], filtered, true);
            }
        }
        return filtered;
    }, [selectedOption, searchOptions, registerFailed, supportsAddNew, supportsLiteralText, copiesHeading, supportsProposeAuthority, createsPublished, filterText, intl]);
    /**
     * Custom Selection formatter so the create affordance ("+ Add new term" /
     * "+ Navrhnout založení GA") visually stands out (separator below + accent +
     * bold). All other options render with the default plain label — proposal
     * options already carry their "(návrh)" suffix in the label from the hook.
     */
    var optionFormatter = useCallback(function (selection) {
        var option = selection.option;
        if (!option)
            return null;
        if (option.value.startsWith(ADD_NEW_TERM_PREFIX) || option.value.startsWith(PROPOSE_AUTHORITY_PREFIX)
            || option.value.startsWith(LITERAL_TEXT_PREFIX)) {
            return React.createElement("div", { style: __assign(__assign({}, ADD_NEW_TERM_ROW_STYLE), ADD_NEW_TERM_LABEL_STYLE) }, option.label);
        }
        // CZNA-1033: a variant row shows what was found and, set apart, what picking it stores.
        if (option.found && option.heading && option.found !== option.heading) {
            return (React.createElement("div", null,
                option.found,
                ' ',
                React.createElement("span", { style: INSERTS_SUFFIX_STYLE }, insertsSuffix(option.heading, intl))));
        }
        return React.createElement("div", null, option.label);
    }, [intl]);
    var handleVocabularyCreated = useCallback(function (created) {
        setVocabularyFormOpen(false);
        // TASK-151: also dismiss the picker modal — when the user triggered
        // create from inside RecordPickerModal (the "+ Add new term" footer
        // button), the picker stays mounted behind VocabularyForm and must
        // close on success so the new pick lands cleanly in the form below.
        // For the autocomplete-dropdown entry path the modal is already closed,
        // so this is a no-op there.
        setModalOpen(false);
        if ((created === null || created === void 0 ? void 0 : created.id) && (created === null || created === void 0 ? void 0 : created.name)) {
            // Treat the freshly-created vocabulary item identically to picking an existing one
            // — including CZNA-940's code rule, so a term created here and the same term picked
            // from the register store the same string. Unreachable for the language register
            // (closed, no allowFreeText), which is exactly why it must not be special-cased.
            handlePickRef.current(created.id, created.name, created.code);
            setFilterText('');
        }
    }, []);
    return (React.createElement(Controller, { name: subfieldPath, control: control, render: function (_a) {
            var field = _a.field;
            var subVal = (field.value || { value: '', sourceAuthorityId: '' });
            var handlePick = function (selectedId, selectedHeading, selectedCode, stored) {
                // Vocabulary/authority pick — store text + reference (FK).
                //
                // CZNA-940: "text" is the heading for every register whose subfield stores the
                // heading, and the CODE for a code-bearing one (language, 377$a / 41#$9). The
                // heading is still what the option is FILTERED by, so a cataloguer keeps
                // finding the term by its name while the record keeps the code.
                //
                // CZNA-1430: `stored` is this subfield's part of a heading copied part by part.
                var storedValue = stored !== null && stored !== void 0 ? stored : vocabularyStoredValue(recordType, selectedHeading, selectedCode, vocabularyTypes);
                pickSeqRef.current += 1;
                field.onChange(__assign(__assign({}, subVal), { value: storedValue, sourceAuthorityId: selectedId }));
                setSelectedOption({
                    value: selectedId,
                    label: vocabularyOptionLabel(selectedHeading, selectedCode),
                    heading: selectedHeading,
                    code: selectedCode !== null && selectedCode !== void 0 ? selectedCode : null,
                });
                syncedAuthorityIdRef.current = selectedId;
                syncedValueRef.current = storedValue;
            };
            // Expose the latest closure to the create-modal success handler. We can't pass
            // handlePick directly through useCallback deps (field.onChange is stable but
            // subVal changes every render); a ref is the simplest non-stale bridge.
            handlePickRef.current = handlePick;
            /**
             * CZNA-1033: a pick from the register. Its rows carry no vocabulary code, so a
             * code-bearing register (CZNA-940) reads the code from the picked record now —
             * one request per pick. `code === undefined` means "not known yet"; a known
             * `null` (the term has none) stores the heading, as before. If the read fails,
             * nothing is stored: the heading in a code subfield would be a wrong value.
             */
            var pickFromRegister = function (selectedId, selectedHeading, selectedCode) {
                setCodeLoadFailed(false);
                setHeadingLoadFailed(false);
                if (copiesHeading) {
                    // CZNA-1430: the parts come from the picked authority's own heading, one read
                    // per pick. A heading the field cannot hold part by part is stored whole, as
                    // before; a failed read stores nothing, like the code read above it.
                    pickSeqRef.current += 1;
                    var seq_1 = pickSeqRef.current;
                    fetchRecord(ky, selectedId)
                        .then(function (detail) {
                        if (seq_1 !== pickSeqRef.current)
                            return;
                        var copy = linkedHeadingCopy(detail, fieldDef);
                        var stored = copy
                            ? applyLinkedHeadingCopy(copy, fieldDef.tag, pathPrefix, subfieldDef.code, getValues, setValue)
                            : undefined;
                        handlePickRef.current(selectedId, selectedHeading, selectedCode, stored);
                    })
                        .catch(function () {
                        if (seq_1 === pickSeqRef.current)
                            setHeadingLoadFailed(true);
                    });
                    return;
                }
                if (selectedCode !== undefined || !storesVocabularyCode(recordType, vocabularyTypes)) {
                    handlePick(selectedId, selectedHeading, selectedCode);
                    return;
                }
                pickSeqRef.current += 1;
                var seq = pickSeqRef.current;
                fetchRecord(ky, selectedId)
                    .then(function (detail) {
                    if (seq !== pickSeqRef.current)
                        return;
                    handlePickRef.current(selectedId, selectedHeading, vocabularyCodeOf(detail.dataFields));
                })
                    .catch(function () {
                    if (seq === pickSeqRef.current)
                        setCodeLoadFailed(true);
                });
            };
            // CZNA-1430: a linked name's other parts came from the person it was linked to;
            // replacing the name with text, or clearing it, takes them along. An unlinked
            // name's parts were typed by hand and stay.
            var dropLinkedNameParts = function () {
                if (copiesHeading && subVal.sourceAuthorityId) {
                    applyLinkedHeadingCopy(clearedLinkedHeading(fieldDef), fieldDef.tag, pathPrefix, subfieldDef.code, getValues, setValue);
                }
            };
            var handleSelectionChange = function (id) {
                if (id.startsWith(PROPOSE_AUTHORITY_PREFIX)) {
                    // CZNA-241/CZNA-319: hand off to the form host via context — it
                    // snapshots the parent form to sessionStorage and navigates to the
                    // separate create route for THIS picker's target type. No overlay; the
                    // parent's in-progress state is restored on return (and the new
                    // authority's FK lands in THIS subfield). The target type is THIS
                    // picker's `recordType` — `supportsProposeAuthority` already guarantees
                    // it is a member of PROPOSE_AUTHORITY_TARGETS, hence a valid RecordType.
                    var seed = id.slice(PROPOSE_AUTHORITY_PREFIX.length);
                    proposeAuthority(subfieldPath, seed, recordType);
                    return;
                }
                if (id.startsWith(NO_FK_VALUE_PREFIX)) {
                    // CZNA-798: the user re-picked the row that represents the stored,
                    // unlinked value — which is already exactly what the form holds. A no-op
                    // on purpose: `handlePick` below would write the sentinel string into
                    // `sourceAuthorityId`, i.e. persist a reference to a record that does not
                    // exist. Attaching a real FK happens by picking a REAL row, which for a
                    // heading the register holds is the row shown instead of this one.
                    return;
                }
                if (id.startsWith(LITERAL_TEXT_PREFIX)) {
                    // CZNA-1002: keep the typed text as the value, with no FK. The sync
                    // effect above then shows it as the stored, unlinked value it now is.
                    // CZNA-1430: and Selection is remounted. It waits after a pick until the picked
                    // row's value equals `value`, and this row (`__literal__…`) never will — the
                    // stored text comes back as `__nofk__…` — so it kept showing "+ Použít jako
                    // text: …". Worse, in a browser the menu then replays the click on index 0 of
                    // the list the reset below has just rebuilt: no row while the register is
                    // still loading (Selection crashed on an undefined item), or the first
                    // register row (a namesake silently linked). A fresh Selection reads the
                    // stored row like any other unlinked value, and the replay hits nothing.
                    pickSeqRef.current += 1;
                    dropLinkedNameParts();
                    field.onChange(__assign(__assign({}, subVal), { value: id.slice(LITERAL_TEXT_PREFIX.length), sourceAuthorityId: '' }));
                    setFilterText('');
                    setSelectionKey(function (k) { return k + 1; });
                    return;
                }
                if (id.startsWith(ADD_NEW_TERM_PREFIX)) {
                    // Open the create modal seeded with the typed text. Selection internals
                    // close the dropdown automatically when the value changes; we restore
                    // focus implicitly by opening the modal on top.
                    var seed = id.slice(ADD_NEW_TERM_PREFIX.length);
                    setVocabularyFormSeed(seed);
                    setVocabularyFormOpen(true);
                    return;
                }
                var opt = dataOptions.find(function (o) { return o.value === id; });
                if (opt) {
                    pickFromRegister(id, opt.heading, opt.code);
                }
            };
            // Determine the current Selection value. A stored value with no FK (CZNA-798:
            // every imported record, plus pre-TASK-086 drafts) surfaces through the
            // sentinel-prefixed value, so the merged option renders it; new picks always
            // have a real UUID.
            //
            // DISPLAY ONLY — nothing here writes to form state, so opening and saving a
            // record can neither drop the value nor invent an FK for it.
            // CZNA-1474: the linked name of a work — its text, the link, and "Odpojit", which
            // keeps the text (and the parts beside it) and drops only the link. Selection is
            // remounted afterwards, so it reads the text as the unlinked value it now is.
            if (locksName && subVal.sourceAuthorityId) {
                var lockedId = "".concat(pathPrefix, ".").concat(subfieldDef.code, "-linked");
                return (React.createElement("div", { "data-field-path": subfieldPath, "data-linked-name": subfieldDef.code },
                    React.createElement("div", { style: rowStyle },
                        !subfieldDef.formOnly && React.createElement("span", { style: codeStyle },
                            "$",
                            subfieldDef.code),
                        React.createElement("label", { htmlFor: lockedId, style: labelStyle },
                            resolveLabel(subfieldDef, intl),
                            subfieldDef.required && React.createElement("span", { style: { color: '#d0021b', marginLeft: '2px' } }, "*")),
                        React.createElement("input", { id: lockedId, type: "text", style: LOCKED_NAME_STYLE, value: subVal.value || '', title: intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.linkedNameLocked' }), disabled: true }),
                        React.createElement("span", { style: subfieldActionsStyle, "aria-hidden": "true" })),
                    React.createElement("div", { style: UNLINK_ROW_STYLE },
                        React.createElement("button", { type: "button", style: subfieldLinkButtonStyle, onClick: function () {
                                pickSeqRef.current += 1;
                                field.onChange(__assign(__assign({}, subVal), { sourceAuthorityId: '' }));
                                setFilterText('');
                                focusPickerRef.current = true;
                                setSelectionKey(function (k) { return k + 1; });
                            }, "data-testid": "unlink-name" }, intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.unlinkName' })))));
            }
            var selectionValue = subVal.sourceAuthorityId || '';
            if (subVal.value && !subVal.sourceAuthorityId) {
                selectionValue = "".concat(NO_FK_VALUE_PREFIX).concat(subVal.value);
            }
            return (
            // CZNA-241: data-field-path lets the parent's restore effect scroll to
            // this exact subfield after the GA flow writes back its result.
            React.createElement("div", { style: PICKER_ROW_STYLE, "data-field-path": subfieldPath, ref: rowRef },
                !subfieldDef.formOnly && React.createElement("span", { style: codeStyle },
                    "$",
                    subfieldDef.code),
                React.createElement("span", { style: labelStyle },
                    resolveLabel(subfieldDef, intl),
                    subfieldDef.required && React.createElement("span", { style: { color: '#d0021b', marginLeft: '2px' } }, "*")),
                React.createElement("div", { className: css.picker, style: pickerCellStyle },
                    React.createElement(Selection, { key: selectionKey, dataOptions: dataOptions, value: selectionValue, onChange: handleSelectionChange, onFilter: supportsAddNew || asyncFilter ? handleFilter : undefined, asyncFilter: supportsAddNew || asyncFilter, loading: loading, formatter: optionFormatter, placeholder: intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.search' }), marginBottom0: true })),
                React.createElement("span", { style: subfieldActionsStyle },
                    React.createElement(ClearFieldButton, { visible: !!subVal.value, onClear: function () {
                            pickSeqRef.current += 1;
                            dropLinkedNameParts();
                            field.onChange(__assign(__assign({}, subVal), { value: '', sourceAuthorityId: '' }));
                            setSelectedOption(null);
                            syncedAuthorityIdRef.current = '';
                            syncedValueRef.current = '';
                            setFilterText('');
                            setCodeLoadFailed(false);
                            setHeadingLoadFailed(false);
                        } }),
                    React.createElement(IconButton, { icon: "search", onClick: function () { return setModalOpen(true); }, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.browse' }) })),
                (registerFailed || codeLoadFailed || headingLoadFailed) && (React.createElement("div", { style: PICKER_ALERTS_STYLE },
                    registerFailed && (React.createElement("div", { role: "alert", style: CODE_LOAD_ERROR_STYLE }, intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.error' }))),
                    codeLoadFailed && (React.createElement("div", { role: "alert", style: CODE_LOAD_ERROR_STYLE }, intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.codeLoadFailed' }))),
                    headingLoadFailed && (React.createElement("div", { role: "alert", style: CODE_LOAD_ERROR_STYLE }, intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.headingLoadFailed' }))))),
                React.createElement(RecordPickerModal, { open: modalOpen, onClose: function () { return setModalOpen(false); }, onSelect: function (id, heading, code) {
                        pickFromRegister(id, heading, code);
                        setModalOpen(false);
                    }, recordType: recordType, alsoRecordTypes: subfieldDef.alsoRecordTypes, 
                    // CZNA-320: the browse modal offers the same create as the dropdown, under
                    // the same gate and label — "Žádné odpovídající záznamy" in the modal is
                    // exactly where a cataloguer looks for it. An authority target needs typed
                    // text (it seeds the heading), so the button hides until there is some.
                    allowAddNew: supportsAddVocabulary || supportsProposeAuthority, addNewLabel: supportsProposeAuthority
                        ? function (term) {
                            var trimmed = term.trim();
                            return trimmed
                                ? intl.formatMessage({
                                    id: createsPublished
                                        ? 'ui-czna-authorities.recordPicker.createAuthority'
                                        : 'ui-czna-authorities.recordPicker.proposeAuthority',
                                }, { text: trimmed })
                                : null;
                        }
                        : undefined, onAddNew: function (seed) {
                        if (supportsProposeAuthority) {
                            setModalOpen(false);
                            proposeAuthority(subfieldPath, seed.trim(), recordType);
                            return;
                        }
                        // TASK-151: open VocabularyForm seeded with the picker's current
                        // search term. Picker modal stays mounted so a Cancel returns
                        // user to the browser with preserved search context (UX continuity).
                        setVocabularyFormSeed(seed);
                        setVocabularyFormOpen(true);
                    } }),
                vocabularyFormOpen && (React.createElement(VocabularyForm, { defaultType: recordType, lockType: true, defaultName: vocabularyFormSeed, onClose: function () { return setVocabularyFormOpen(false); }, onSuccess: handleVocabularyCreated }))));
        } }));
};
var RecordPickerSubfield = function (_a) {
    var subfieldDef = _a.subfieldDef, pathPrefix = _a.pathPrefix, control = _a.control, fieldDef = _a.fieldDef, getValues = _a.getValues, setValue = _a.setValue;
    // Static-options mode: when selectOptions is provided, this is a small
    // immutable enum (e.g. family qualifier rod/rodina). Bypass API fetch,
    // modal browser, and "+ Add new term" — just render Selection over the
    // static list. Keeps visual identity with the records picker (TASK-115).
    if (subfieldDef.selectOptions && subfieldDef.selectOptions.length > 0) {
        return React.createElement(StaticOptionsPicker, { subfieldDef: subfieldDef, pathPrefix: pathPrefix, control: control });
    }
    return (React.createElement(RegisterPicker, { subfieldDef: subfieldDef, pathPrefix: pathPrefix, control: control, fieldDef: fieldDef, getValues: getValues, setValue: setValue }));
};
export default RecordPickerSubfield;
